import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { scanImagePosts } from '../../utils/postScanner';
import { applyAltText } from '../../utils/altApplier';
import { getGeminiKey, isGeminiConfigured } from '../../utils/altGenerator';
import AltifierPostCard from './AltifierPostCard';

const GEMINI_KEY_STORAGE = 'altifier.geminiKey';
const scanStorageKey = (did) => `altifier.scan.${did}`;

function readSavedScan(did) {
  try {
    const raw = localStorage.getItem(scanStorageKey(did));

    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearSavedScan(did) {
  try {
    localStorage.removeItem(scanStorageKey(did));
  } catch {
    // ignore storage errors
  }
}

export default function Altifier() {
  const { agent, session, loading: isAuthLoading, error: authError } = useAuth();

  const [posts, setPosts] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');
  const [showMissingOnly, setShowMissingOnly] = useState(true);

  // P4 — BYO Gemini key, stored only in localStorage.
  const [geminiKey, setGeminiKey] = useState(() => getGeminiKey());

  // P6 — saved scan progress for the current DID.
  const [savedScan, setSavedScan] = useState(null);

  // --- Load any saved scan for the current DID ---
  useEffect(() => {
    if (session?.did) {
      setSavedScan(readSavedScan(session.did));
    } else {
      setSavedScan(null);
    }
  }, [session?.did]);

  // --- Scan (optionally resuming from a saved cursor) ---
  const handleScan = useCallback(
    async (startCursor) => {
      if (!agent || !session) return;
      setIsScanning(true);
      setStatusMessage('');

      try {
        const onPage = ({ cursor, hits }) => {
          try {
            localStorage.setItem(
              scanStorageKey(session.did),
              JSON.stringify({ cursor, hits })
            );
          } catch {
            // ignore storage errors
          }
        };

        const { posts: results } = await scanImagePosts(agent, session.did, {
          startCursor,
          onPage,
        });

        setPosts(results);
        setSavedScan(null);
        clearSavedScan(session.did);

        const totalMedia = results.reduce((sum, p) => sum + p.images.length, 0);
        const totalMissing = results.reduce((sum, p) => sum + p.missingAltCount, 0);
        const postsWithMissing = results.filter((p) => p.missingAltCount > 0).length;

        setStatusMessage(
          `Scanned ${results.length} posts (${totalMedia} images/media). ` +
            `${postsWithMissing} post${postsWithMissing !== 1 ? 's' : ''} with ${totalMissing} missing alt text.`
        );
        setStatusType('success');
      } catch (err) {
        setStatusMessage(`Scan failed: ${err.message || 'Unknown error'}`);
        setStatusType('error');
      } finally {
        setIsScanning(false);
      }
    },
    [agent, session]
  );

  const handleRestartScan = useCallback(() => {
    clearSavedScan(session?.did);
    setSavedScan(null);
    handleScan(undefined);
  }, [handleScan, session?.did]);

  // --- Apply ---
  const handleApply = useCallback(
    async (post, approvedAlts) => {
      if (!agent || !session) throw new Error('Not authenticated');
      const result = await applyAltText(agent, session.did, post, approvedAlts);

      // Remove the post from the list after successful application
      setPosts((prev) => prev.filter((p) => p.uri !== post.uri));
      setStatusMessage(
        `Post recreated with alt text. The original post's engagement (likes, reposts, quotes, replies) was lost.`
      );
      setStatusType('success');

      return result;
    },
    [agent, session]
  );

  // --- Gemini key settings (P4) ---
  const handleSaveGeminiKey = useCallback(() => {
    try {
      localStorage.setItem(GEMINI_KEY_STORAGE, geminiKey.trim());
    } catch {
      setStatusMessage('Could not save the Gemini API key to localStorage.');
      setStatusType('error');

      return;
    }

    setStatusMessage('Gemini API key saved. Generate buttons are now enabled.');
    setStatusType('success');
  }, [geminiKey]);

  const handleClearGeminiKey = useCallback(() => {
    try {
      localStorage.removeItem(GEMINI_KEY_STORAGE);
    } catch {
      // ignore storage errors
    }

    setGeminiKey('');
    setStatusMessage('Gemini API key cleared. Generate buttons are hidden.');
    setStatusType('success');
  }, []);

  // --- Derived ---
  const displayPosts = showMissingOnly
    ? posts.filter((p) => p.missingAltCount > 0)
    : posts;

  const totalMedia = posts.reduce((sum, p) => sum + p.images.length, 0);
  const totalMissing = posts.reduce((sum, p) => sum + p.missingAltCount, 0);
  const postsWithMissing = posts.filter((p) => p.missingAltCount > 0).length;
  const geminiEnabled = isGeminiConfigured();

  // --- Render ---
  if (isAuthLoading) return <p>Loading authentication...</p>;

  if (authError)
    return (
      <p>
        Authentication Error: {authError}. <a href="/login">Please login</a>.
      </p>
    );

  return (
    <div className="altifier-container">
      <div className="altifier-section">
        <h2>Alt Text Scanner</h2>
        <p>
          Scan your Bluesky posts and replies for images and videos that are
          missing alt text. Generated alt text follows{' '}
          <a
            href="https://www.section508.gov/create/alternative-text/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Section 508 guidelines
          </a>
          . Applying alt text recreates the post (delete +
          new post), so the original&apos;s likes, reposts, quotes, and replies are
          lost.
        </p>

        <button
          onClick={() => handleScan(undefined)}
          disabled={isScanning}
          className="altifier-scan-button"
        >
          {isScanning ? 'Scanning...' : 'Scan My Posts'}
        </button>
      </div>

      {savedScan && posts.length === 0 && !isScanning && (
        <div className="altifier-section altifier-resume-box">
          <p>
            A previous scan found {savedScan.hits} item
            {savedScan.hits !== 1 ? 's' : ''} so far.
          </p>
          <div className="altifier-resume-buttons">
            <button
              onClick={() => handleScan(savedScan.cursor)}
              disabled={isScanning}
              className="altifier-scan-button"
            >
              Resume scan ({savedScan.hits} found so far)
            </button>
            <button
              onClick={handleRestartScan}
              disabled={isScanning}
              className="altifier-resume-restart"
            >
              Restart
            </button>
          </div>
        </div>
      )}

      {isScanning && (
        <div className="altifier-status-box altifier-status-box-success">
          <p>Scanning your posts for images...</p>
        </div>
      )}

      {statusMessage && (
        <div className={`altifier-status-box altifier-status-box-${statusType}`}>
          <p>{statusMessage}</p>
        </div>
      )}

      <div className="altifier-section altifier-settings">
        <h3>AI Settings</h3>
        <label className="altifier-settings-label" htmlFor="gemini-key">
          Gemini API key (optional)
        </label>
        <input
          id="gemini-key"
          type="password"
          value={geminiKey}
          onChange={(e) => setGeminiKey(e.target.value)}
          placeholder="Paste your Gemini API key..."
          className="altifier-settings-input"
        />
        <p className="altifier-settings-note">
          Stored only in your browser (localStorage) and never leaves this
          device except in direct calls to Google. If no key is set, Generate
          buttons are hidden and you can still type alt text manually.
        </p>
        <div className="altifier-settings-buttons">
          <button
            onClick={handleSaveGeminiKey}
            disabled={!geminiKey.trim()}
            className="altifier-settings-save"
          >
            Save Key
          </button>
          <button
            onClick={handleClearGeminiKey}
            disabled={!geminiEnabled && !geminiKey}
            className="altifier-settings-clear"
          >
            Clear Key
          </button>
        </div>
      </div>

      {posts.length > 0 && (
        <div className="altifier-section">
          <div className="altifier-summary">
            <h3>Summary</h3>
            <ul>
              <li>{posts.length} posts found ({totalMedia} images/media)</li>
              <li>{postsWithMissing} post{postsWithMissing !== 1 ? 's' : ''} with missing alt text</li>
              <li>{totalMissing} image{totalMissing !== 1 ? 's' : ''} missing alt text</li>
              <li>{posts.length - postsWithMissing} post{(posts.length - postsWithMissing) !== 1 ? 's' : ''} already have alt text on all media</li>
            </ul>
          </div>

          {postsWithMissing > 0 && (
            <label className="altifier-filter">
              <input
                type="checkbox"
                checked={showMissingOnly}
                onChange={(e) => setShowMissingOnly(e.target.checked)}
              />{' '}
              Show only posts with missing alt text
            </label>
          )}

          <h3>
            {showMissingOnly
              ? `Posts Missing Alt Text (${postsWithMissing})`
              : `All Posts (${posts.length})`}
          </h3>

          <div className="altifier-post-list">
            {displayPosts.map((post) => (
              <AltifierPostCard
                key={post.uri}
                post={post}
                sessionDid={session?.did}
                onApply={handleApply}
                geminiEnabled={geminiEnabled}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
